import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Heading,
  Icon,
  Button,
  Spinner,
  Skeleton,
  Badge,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  useToast,
  useBreakpointValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Progress,
} from "@chakra-ui/react";
import { 
  FiDollarSign, 
  FiPercent, 
  FiShoppingCart, 
  FiTrendingUp, 
  FiTrendingDown, 
  FiMinus, 
  FiSearch, 
  FiFilter, 
  FiArrowLeft,
  FiCreditCard,
  FiTruck,
  FiGift,
  FiTag,
  FiAlertCircle,
  FiRefreshCw,
  FiDownload,
  FiEye,
  FiCalendar,
  FiSmartphone,
  FiMonitor,
  FiPackage,
  FiTruck as FiDelivery,
  FiInfo,
  FiUsers,
  FiCheckCircle,
  FiClock,
  FiXCircle
} from "react-icons/fi";
import { Pie, Doughnut, Bar } from 'react-chartjs-2';
import "../../theme/Layout/ChartConfig";
import API from "../../context/API";
import EarningsAPI from "../../APIs/earningsAPI";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../../commonComponents/DateRangePicker";

const MerchantEarningsOverview = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedMerchantDetails, setSelectedMerchantDetails] = useState(null);
  
  // Color mode values
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const successColor = useColorModeValue("green.500", "green.400");
  const warningColor = useColorModeValue("orange.500", "orange.400");
  const dangerColor = useColorModeValue("red.500", "red.400");
  const infoColor = useColorModeValue("blue.500", "blue.400");
  const errorBgColor = useColorModeValue("red.50", "red.900");
  const errorIconColor = useColorModeValue("red.500", "red.300");
  const noDataBgColor = useColorModeValue("blue.50", "blue.900");
  const noDataIconColor = useColorModeValue("blue.500", "blue.300");

  // State
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: moment().subtract(7, "days"),
    endDate: moment(),
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("netEarnings");
  const [sortOrder, setSortOrder] = useState("desc");
  const [activeTab, setActiveTab] = useState(0);
  const [summaryStats, setSummaryStats] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState(null);

  // Fetch admin merchant earnings (server-side pagination/sorting)
  const fetchMerchantsEarnings = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await EarningsAPI.getAdminMerchantEarnings({
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate,
        page,
        limit,
        sortBy,
        sortOrder,
      });

      const data = resp?.data || resp; // support both wrapped and direct
      const pag = resp?.pagination || data?.pagination;
      const totals = resp?.totals || data?.totals;
      const list = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(data?.data)
          ? data.data
          : [];

      setRows(list);
      setPagination(pag || null);
      if (totals) {
        setSummaryStats({
          totalRevenue: totals.totalRevenue || 0,
          totalFees: totals.totalFees || 0,
          totalNetEarnings: totals.netEarnings || 0,
          totalOrders: totals.orderCount || 0,
          merchantCount: (pag?.totalItems ?? list.length),
          averageOrderValue: (totals.orderCount || 0) > 0 ? (totals.totalRevenue || 0) / (totals.orderCount || 0) : 0
        });
      } else {
        setSummaryStats(null);
      }

    } catch (error) {
      console.error("Error fetching merchants earnings:", error);
      setError(error.message || "Failed to fetch merchants earnings data");
      
      toast({
        title: "Error",
        description: error.message || "Failed to fetch merchants earnings data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchantsEarnings();
  }, [selectedDateRange, page, limit, sortBy, sortOrder]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Payment status not supported yet; column removed

  // Filter and sort merchants
  const filteredMerchants = (Array.isArray(rows) ? rows : [])
    .filter(r =>
      (r.merchantName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleOpenDetails = async (row) => {
    setSelectedMerchant(row);
    setSelectedMerchantDetails(null);
    onOpen();
    try {
      const details = await EarningsAPI.getMerchantEarnings(row.merchantId, {
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate,
      });
      setSelectedMerchantDetails(details?.data || details);
    } catch (e) {
      toast({
        title: "Failed to load merchant details",
        description: e.message || 'Error fetching details',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Export to CSV via backend endpoint
  const handleExportCSV = async () => {
    try {
      const csvText = await EarningsAPI.exportAdminMerchantEarningsCSV({
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate,
        sortBy,
        sortOrder,
      });

      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `merchants-earnings-${moment().format('YYYY-MM-DD')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: "Merchants earnings data exported to CSV",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: error.message || "Failed to export earnings data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading && !rows.length) {
    return (
      <Box bg={bgColor} minH="100vh" p={{ base: 2, md: 4 }}>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW="1400px" mx="auto">
          <Flex justify="center" align="center" minH="50vh">
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" />
              <Text color={subTextColor} fontSize={{ base: "sm", md: "md" }}>
                Loading merchants earnings data...
              </Text>
            </VStack>
          </Flex>
        </VStack>
      </Box>
    );
  }

  // Error state
  if (error && !rows.length) {
    return (
      <Box bg={bgColor} minH="100vh" p={{ base: 2, md: 4 }}>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW="1400px" mx="auto">
          {/* Header */}
          <Flex 
            justify="flex-start" 
            align="center"
            direction={{ base: "column", sm: "row" }}
            gap={{ base: 3, sm: 4 }}
          >
            <IconButton
              icon={<FiArrowLeft />}
              variant="outline"
              color={textColor}
              borderRadius={"full"}
              onClick={() => navigate(-1)}
              _hover={{ bg: "gray.200" }}
              aria-label="Go Back to Admin Dashboard"
              size={{ base: "sm", md: "md" }}
              alignSelf={{ base: "flex-start", sm: "center" }}
            />
            <VStack align={{ base: "center", sm: "start" }} spacing={1}>
              <Heading 
                size={{ base: "md", md: "lg" }} 
                color={textColor}
                textAlign={{ base: "center", sm: "left" }}
              >
                Merchant Earnings Overview
              </Heading>
              <Text 
                color={subTextColor}
                fontSize={{ base: "sm", md: "md" }}
                textAlign={{ base: "center", sm: "left" }}
              >
                Track all merchant earnings and payouts
              </Text>
            </VStack>
          </Flex>

          {/* Error State */}
          <Card bg={cardBg}>
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack spacing={6} align="center" textAlign="center">
                <Box
                  p={6}
                  borderRadius="full"
                  bg={errorBgColor}
                  color={errorIconColor}
                >
                  <Icon as={FiAlertCircle} boxSize={12} />
                </Box>
                
                <VStack spacing={3}>
                  <Heading 
                    size={{ base: "md", md: "lg" }} 
                    color={textColor}
                  >
                    Error Loading Data
                  </Heading>
                  <Text 
                    color={subTextColor}
                    fontSize={{ base: "sm", md: "md" }}
                    maxW="500px"
                  >
                    {error}
                  </Text>
                </VStack>

                <HStack spacing={4} flexWrap="wrap" justify="center">
                  <Button
                    colorScheme="blue"
                    onClick={fetchMerchantsEarnings}
                    isLoading={loading}
                    leftIcon={<FiRefreshCw />}
                    size={{ base: "sm", md: "md" }}
                  >
                    Try Again
                  </Button>
                  <Button
                    colorScheme="gray"
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    leftIcon={<FiArrowLeft />}
                    size={{ base: "sm", md: "md" }}
                  >
                    Go Back
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" p={{ base: 2, md: 4 }}>
      <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW="1400px" mx="auto">
        {/* Header */}
        <Flex 
          justify="space-between" 
          align="center"
          direction={{ base: "column", sm: "row" }}
          gap={{ base: 3, sm: 4 }}
        >
          <Flex 
            justify="flex-start" 
            align="center"
            direction={{ base: "column", sm: "row" }}
            gap={{ base: 3, sm: 4 }}
          >
            <IconButton
              icon={<FiArrowLeft />}
              variant="outline"
              color={textColor}
              borderRadius={"full"}
              onClick={() => navigate(-1)}
              _hover={{ bg: "gray.200" }}
              aria-label="Go Back to Admin Dashboard"
              size={{ base: "sm", md: "md" }}
              alignSelf={{ base: "flex-start", sm: "center" }}
            />
            <VStack align={{ base: "center", sm: "start" }} spacing={1}>
              <Heading 
                size={{ base: "md", md: "lg" }} 
                color={textColor}
                textAlign={{ base: "center", sm: "left" }}
              >
                Merchant Earnings Overview
              </Heading>
              <Text 
                color={subTextColor}
                fontSize={{ base: "sm", md: "md" }}
                textAlign={{ base: "center", sm: "left" }}
              >
                Track all merchant earnings and payouts • {selectedDateRange.startDate.format('MMM DD')} to {selectedDateRange.endDate.format('MMM DD')}
              </Text>
            </VStack>
          </Flex>
          
          <HStack spacing={2}>
            <Button
              leftIcon={<FiDownload />}
              colorScheme="blue"
              variant="outline"
              onClick={handleExportCSV}
              size={{ base: "sm", md: "md" }}
            >
              Export CSV
            </Button>
            <Button
              leftIcon={<FiRefreshCw />}
              colorScheme="blue"
              onClick={fetchMerchantsEarnings}
              isLoading={loading}
              size={{ base: "sm", md: "md" }}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>

        {/* Filters */}
        <Card bg={cardBg}>
          <CardBody p={{ base: 3, md: 4 }}>
            <VStack spacing={4} align="stretch">
              <Flex
                direction={{ base: "column", lg: "row" }}
                gap={{ base: 3, md: 4 }}
                wrap="wrap"
                align={{ base: "stretch", lg: "end" }}
              >
                <Box flex="1" minW={{ base: "100%", sm: "200px" }}>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color={textColor}>
                    Date Range
                  </Text>
                  <DateRangePicker
                    selectedDateRange={selectedDateRange}
                    onDateRangeChange={setSelectedDateRange}
                  />
                </Box>
                <Box minW={{ base: "100%", sm: "200px" }}>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color={textColor}>
                    Search Merchants
                  </Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg={cardBg}
                      borderColor={borderColor}
                    />
                  </InputGroup>
                </Box>
                <Box minW={{ base: "100%", sm: "150px" }}>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color={textColor}>
                    Sort By
                  </Text>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    bg={cardBg}
                    borderColor={borderColor}
                  >
                    <option value="netEarnings">Net Earnings</option>
                    <option value="totalRevenue">Total Revenue</option>
                    <option value="totalFees">Total Fees</option>
                    <option value="orderCount">Order Count</option>
                  </Select>
                </Box>
                <Box minW={{ base: "100%", sm: "120px" }}>
                  <Text fontSize="sm" fontWeight="medium" mb={2} color={textColor}>
                    Order
                  </Text>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    bg={cardBg}
                    borderColor={borderColor}
                  >
                    <option value="desc">High to Low</option>
                    <option value="asc">Low to High</option>
                  </Select>
                </Box>
              </Flex>
            </VStack>
          </CardBody>
        </Card>

        {/* Summary Stats */}
        {summaryStats && (
          <SimpleGrid 
            columns={{ base: 1, sm: 2, lg: 4 }} 
            spacing={{ base: 3, md: 4 }}
            gap={{ base: 3, md: 4 }}
          >
            <Card bg={cardBg}>
              <CardBody p={{ base: 3, md: 4 }}>
                <Stat>
                  <StatLabel 
                    color={subTextColor}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    Total Revenue
                  </StatLabel>
                  <StatNumber 
                    color={textColor}
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                  >
                    {formatCurrency(summaryStats.totalRevenue)}
                  </StatNumber>
                  <StatHelpText fontSize={{ base: "xs", md: "sm" }}>
                    <Icon as={FiDollarSign} mr={1} />
                    Across {summaryStats.merchantCount} merchants
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={cardBg}>
              <CardBody p={{ base: 3, md: 4 }}>
                <Stat>
                  <StatLabel 
                    color={subTextColor}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    Total Fees
                  </StatLabel>
                  <StatNumber 
                    color={dangerColor}
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                  >
                    {formatCurrency(summaryStats.totalFees)}
                  </StatNumber>
                  <StatHelpText fontSize={{ base: "xs", md: "sm" }}>
                    <Icon as={FiPercent} mr={1} />
                    {formatPercentage((summaryStats.totalFees / summaryStats.totalRevenue) * 100)} of revenue
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={cardBg}>
              <CardBody p={{ base: 3, md: 4 }}>
                <Stat>
                  <StatLabel 
                    color={subTextColor}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    Net Payouts Due
                  </StatLabel>
                  <StatNumber 
                    color={successColor}
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                  >
                    {formatCurrency(summaryStats.totalNetEarnings)}
                  </StatNumber>
                  <StatHelpText fontSize={{ base: "xs", md: "sm" }}>
                    <Icon as={FiUsers} mr={1} />
                    Amount to pay merchants
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={cardBg}>
              <CardBody p={{ base: 3, md: 4 }}>
                <Stat>
                  <StatLabel 
                    color={subTextColor}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    Total Orders
                  </StatLabel>
                  <StatNumber 
                    color={textColor}
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                  >
                    {summaryStats.totalOrders?.toLocaleString() || 0}
                  </StatNumber>
                  <StatHelpText fontSize={{ base: "xs", md: "sm" }}>
                    <Icon as={FiShoppingCart} mr={1} />
                    Avg: {formatCurrency(summaryStats.averageOrderValue)}
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>
        )}

        {/* Merchants Table */}
        <Card bg={cardBg}>
          <CardBody p={{ base: 3, md: 4 }}>
            <VStack spacing={4} align="stretch">
              <Heading 
                size={{ base: "sm", md: "md" }} 
                color={textColor}
                textAlign={{ base: "center", md: "left" }}
              >
                Merchants Earnings Breakdown
              </Heading>
              <TableContainer 
                overflowX="auto"
                borderRadius="md"
                border="1px solid"
                borderColor={borderColor}
              >
                <Table 
                  variant="simple" 
                  size={{ base: "sm", md: "md" }}
                  minW="1000px"
                >
                  <Thead>
                    <Tr>
                      <Th 
                        color={textColor}
                        fontSize={{ base: "xs", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                      >
                        Merchant
                      </Th>
                      <Th 
                        color={textColor} 
                        isNumeric
                        fontSize={{ base: "xs", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                      >
                        Revenue
                      </Th>
                      <Th 
                        color={textColor} 
                        isNumeric
                        fontSize={{ base: "xs", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                      >
                        Fees
                      </Th>
                      <Th 
                        color={textColor} 
                        isNumeric
                        fontSize={{ base: "xs", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                      >
                        Net Earnings
                      </Th>
                      <Th 
                        color={textColor} 
                        isNumeric
                        fontSize={{ base: "xs", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                      >
                        Orders
                      </Th>
                      
                      <Th 
                        color={textColor}
                        fontSize={{ base: "xs", md: "sm" }}
                        py={{ base: 2, md: 3 }}
                      >
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredMerchants.map((row, index) => (
                      <Tr key={row.merchantId || index}>
                        <Td py={{ base: 2, md: 3 }}>
                          <VStack align="start" spacing={1}>
                            <Text 
                              fontWeight="bold"
                              fontSize={{ base: "xs", md: "sm" }}
                              color={textColor}
                            >
                              {row.merchantName}
                            </Text>
                            <Text 
                              fontSize={{ base: "xs", md: "sm" }}
                              color={subTextColor}
                            >
                              {row.email}
                            </Text>
                          </VStack>
                        </Td>
                        <Td 
                          isNumeric 
                          color={textColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          py={{ base: 2, md: 3 }}
                        >
                          {formatCurrency(row.totalRevenue)}
                        </Td>
                        <Td 
                          isNumeric 
                          color={dangerColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          py={{ base: 2, md: 3 }}
                        >
                          {formatCurrency(row.totalFees)}
                        </Td>
                        <Td 
                          isNumeric 
                          color={successColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          py={{ base: 2, md: 3 }}
                          fontWeight="bold"
                        >
                          {formatCurrency(row.netEarnings)}
                        </Td>
                        <Td 
                          isNumeric 
                          color={textColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          py={{ base: 2, md: 3 }}
                        >
                          {row.orderCount?.toLocaleString?.() || row.orderCount || 0}
                        </Td>
                        
                        <Td py={{ base: 2, md: 3 }}>
                          <HStack spacing={1}>
                            <Button
                              size="xs"
                              variant="ghost"
                              leftIcon={<FiEye />}
                              onClick={() => handleOpenDetails(row)}
                            >
                              View
                            </Button>
                            {/* Mark Paid feature not implemented; removed to avoid confusion */}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              {pagination && (
                <HStack justify="space-between" mt={3}>
                  <Text fontSize="xs" color={subTextColor}>
                    Page {pagination.currentPage} of {pagination.totalPages} • {pagination.totalItems} items
                  </Text>
                  <HStack>
                    <Button
                      size="sm"
                      variant="outline"
                      isDisabled={!pagination.hasPreviousPage}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      isDisabled={!pagination.hasNextPage}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </HStack>
                </HStack>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Merchant Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Merchant Details: {selectedMerchant?.merchantName}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!selectedMerchantDetails && (
              <VStack spacing={4} align="center" py={6}>
                <Spinner />
                <Text color={subTextColor} fontSize="sm">Loading details…</Text>
              </VStack>
            )}
            {selectedMerchantDetails && (
              <VStack spacing={6} align="stretch">
                {/* Summary Stats */}
                <SimpleGrid columns={2} spacing={4}>
                  <Stat>
                    <StatLabel>Total Revenue</StatLabel>
                    <StatNumber color={textColor}>
                      {formatCurrency(selectedMerchantDetails.summary?.totalRevenue)}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Total Fees</StatLabel>
                    <StatNumber color={dangerColor}>
                      {formatCurrency(selectedMerchantDetails.summary?.totalFees)}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Net Earnings</StatLabel>
                    <StatNumber color={successColor}>
                      {formatCurrency(selectedMerchantDetails.summary?.netEarnings)}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Order Count</StatLabel>
                    <StatNumber>
                      {selectedMerchantDetails.summary?.orderCount?.toLocaleString() || 0}
                    </StatNumber>
                  </Stat>
                </SimpleGrid>

                <Divider />

                {/* Platform Breakdown */}
                <Box>
                  <Heading size="sm" mb={3}>Platform Breakdown</Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    <Card bg="blue.50" border="1px solid" borderColor="blue.200">
                      <CardBody p={3}>
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={2}>
                            <Icon as={FiMonitor} color="blue.500" boxSize={4} />
                            <Text fontWeight="bold">Kiosk</Text>
                          </HStack>
                          <Badge colorScheme="blue">{selectedMerchantDetails.platformBreakdown?.kiosk?.orderCount || 0} orders</Badge>
                        </HStack>
                        <VStack spacing={1} align="stretch">
                          <HStack justify="space-between">
                            <Text fontSize="sm">Revenue:</Text>
                            <Text fontWeight="bold">{formatCurrency(selectedMerchantDetails.platformBreakdown?.kiosk?.revenue)}</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Fees:</Text>
                            <Text color={dangerColor}>{formatCurrency(selectedMerchantDetails.platformBreakdown?.kiosk?.fees)}</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Net:</Text>
                            <Text fontWeight="bold" color={successColor}>{formatCurrency(selectedMerchantDetails.platformBreakdown?.kiosk?.netEarnings)}</Text>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card bg="green.50" border="1px solid" borderColor="green.200">
                      <CardBody p={3}>
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={2}>
                            <Icon as={FiSmartphone} color="green.500" boxSize={4} />
                            <Text fontWeight="bold">Mobile</Text>
                          </HStack>
                          <Badge colorScheme="green">{selectedMerchantDetails.platformBreakdown?.mobile?.orderCount || 0} orders</Badge>
                        </HStack>
                        <VStack spacing={1} align="stretch">
                          <HStack justify="space-between">
                            <Text fontSize="sm">Revenue:</Text>
                            <Text fontWeight="bold">{formatCurrency(selectedMerchantDetails.platformBreakdown?.mobile?.revenue)}</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Fees:</Text>
                            <Text color={dangerColor}>{formatCurrency(selectedMerchantDetails.platformBreakdown?.mobile?.fees)}</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Net:</Text>
                            <Text fontWeight="bold" color={successColor}>{formatCurrency(selectedMerchantDetails.platformBreakdown?.mobile?.netEarnings)}</Text>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MerchantEarningsOverview;

